/**
 * PipelineView — 파이프라인 실시간 진행 상황 대시보드
 * 각 에이전트 단계를 시각적으로 표시
 */

import React from 'react';
import { clsx } from 'clsx';
import {
  Search, FileText, Camera, Film, Mic, ShieldCheck,
  CheckCircle2, Loader2, XCircle, Circle, Clock, Zap,
} from 'lucide-react';

// ============================================================
// Types
// ============================================================

interface PipelineStep {
  id: string;
  step_name: string;
  agent: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  error_message: string | null;
}

interface PipelineViewProps {
  projectId: string;
  status: string;
  progress: number;
  steps: PipelineStep[];
  onApprove?: (approved: boolean, notes?: string) => void;
  onStop?: () => void;
}

// ============================================================
// Agent Step Configuration
// ============================================================

const PIPELINE_STAGES = [
  { agent: 'scout', label: '사이트 탐방', icon: Search, color: 'blue' },
  { agent: 'strategist', label: '시나리오 기획', icon: FileText, color: 'purple' },
  { agent: 'director', label: '장면 설계', icon: Camera, color: 'orange' },
  { agent: 'narrator', label: '내레이션', icon: Mic, color: 'green' },
  { agent: 'master', label: '영상 생성', icon: Film, color: 'pink' },
  { agent: 'qa', label: '품질 검수', icon: ShieldCheck, color: 'indigo' },
];

// ============================================================
// Component
// ============================================================

export function PipelineView({
  projectId, status, progress, steps, onApprove, onStop,
}: PipelineViewProps) {
  const [revisionNotes, setRevisionNotes] = React.useState('');
  const isApprovalGate = status === 'approval_gate';
  const isComplete = status === 'complete';
  const isError = status === 'error';

  const getStepStatus = (agent: string) => {
    const agentSteps = steps.filter(s => s.agent === agent);
    if (agentSteps.some(s => s.status === 'running')) return 'running';
    if (agentSteps.some(s => s.status === 'completed')) return 'completed';
    if (agentSteps.some(s => s.status === 'failed')) return 'failed';
    return 'pending';
  };

  const getStepDuration = (agent: string) => {
    const completed = steps.find(s => s.agent === agent && s.status === 'completed');
    if (completed?.duration_ms) {
      const sec = Math.round(completed.duration_ms / 1000);
      return sec < 60 ? `${sec}s` : `${Math.round(sec / 60)}m ${sec % 60}s`;
    }
    return null;
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">파이프라인 진행 상황</h2>
          <p className="text-sm text-gray-500 mt-1">
            {isComplete ? '영상 생성 완료!' :
             isError ? '오류가 발생했습니다' :
             isApprovalGate ? '시나리오 승인을 기다리고 있습니다' :
             '에이전트가 영상을 만들고 있습니다...'}
          </p>
        </div>

        {!isComplete && !isError && onStop && (
          <button
            onClick={onStop}
            className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition"
          >
            중단
          </button>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-gray-600">전체 진행률</span>
          <span className="font-medium text-gray-900">{progress}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={clsx(
              'h-full rounded-full transition-all duration-700 ease-out',
              isComplete ? 'bg-green-500' :
              isError ? 'bg-red-500' :
              'bg-gradient-to-r from-blue-500 to-purple-500'
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Pipeline Steps */}
      <div className="space-y-3">
        {PIPELINE_STAGES.map((stage, index) => {
          const stepStatus = getStepStatus(stage.agent);
          const duration = getStepDuration(stage.agent);
          const Icon = stage.icon;

          return (
            <div
              key={stage.agent}
              className={clsx(
                'flex items-center gap-4 p-3 rounded-xl transition-all',
                stepStatus === 'running' && 'bg-blue-50 border border-blue-100',
                stepStatus === 'completed' && 'bg-gray-50',
                stepStatus === 'failed' && 'bg-red-50 border border-red-100',
                stepStatus === 'pending' && 'opacity-50',
              )}
            >
              {/* Step Number + Connection Line */}
              <div className="relative flex flex-col items-center">
                <div className={clsx(
                  'w-10 h-10 rounded-full flex items-center justify-center',
                  stepStatus === 'running' && 'bg-blue-100 text-blue-600',
                  stepStatus === 'completed' && 'bg-green-100 text-green-600',
                  stepStatus === 'failed' && 'bg-red-100 text-red-600',
                  stepStatus === 'pending' && 'bg-gray-100 text-gray-400',
                )}>
                  {stepStatus === 'running' ? <Loader2 className="w-5 h-5 animate-spin" /> :
                   stepStatus === 'completed' ? <CheckCircle2 className="w-5 h-5" /> :
                   stepStatus === 'failed' ? <XCircle className="w-5 h-5" /> :
                   <Icon className="w-5 h-5" />}
                </div>
                {index < PIPELINE_STAGES.length - 1 && (
                  <div className={clsx(
                    'absolute top-10 w-0.5 h-6',
                    stepStatus === 'completed' ? 'bg-green-200' : 'bg-gray-200'
                  )} />
                )}
              </div>

              {/* Step Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{stage.label}</span>
                  {stepStatus === 'running' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                      <Zap className="w-3 h-3" /> 진행 중
                    </span>
                  )}
                </div>
                {stepStatus === 'failed' && (
                  <p className="text-xs text-red-600 mt-0.5">
                    {steps.find(s => s.agent === stage.agent && s.status === 'failed')?.error_message || '실행 실패'}
                  </p>
                )}
              </div>

              {/* Duration */}
              {duration && (
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Clock className="w-3 h-3" />
                  {duration}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Approval Gate UI */}
      {isApprovalGate && onApprove && (
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <h3 className="font-medium text-amber-800 mb-2">시나리오 승인 요청</h3>
          <p className="text-sm text-amber-700 mb-4">
            AI가 생성한 데모 시나리오를 검토하고 승인해주세요.
            수정이 필요하면 피드백을 남겨주세요.
          </p>

          <textarea
            value={revisionNotes}
            onChange={(e) => setRevisionNotes(e.target.value)}
            placeholder="수정 요청사항 (선택)"
            className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm mb-3 resize-none focus:outline-none focus:ring-2 focus:ring-amber-300"
            rows={3}
          />

          <div className="flex gap-2">
            <button
              onClick={() => onApprove(true)}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition"
            >
              승인하고 영상 제작 시작
            </button>
            <button
              onClick={() => onApprove(false, revisionNotes)}
              className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition"
            >
              수정 요청
            </button>
          </div>
        </div>
      )}

      {/* Complete State */}
      {isComplete && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl text-center">
          <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
          <h3 className="font-semibold text-green-800">데모 영상 완성!</h3>
          <p className="text-sm text-green-600 mt-1 mb-4">
            랜딩페이지에 바로 삽입할 수 있는 영상이 준비되었습니다.
          </p>
          <div className="flex gap-2 justify-center">
            <button className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition">
              영상 다운로드
            </button>
            <button className="px-4 py-2 bg-white text-green-700 border border-green-300 rounded-lg text-sm font-medium hover:bg-green-50 transition">
              임베드 코드 복사
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
